/**
 * Navigation Style Compositions
 * Building blocks combined into final class strings
 */

import { COLORS, TYPOGRAPHY, LAYOUT, EFFECTS, STATES } from "./styleConstants";

// Helper function to combine class arrays into strings
export const combineClasses = (...classArrays) => {
  return classArrays.flat().join(" ");
};

// Base combinations used across multiple variants
export const BASE_COMBINATIONS = {
  // Common text and hover pattern
  textWithHover: [COLORS.primary, COLORS.primaryHover],
  textSecondaryWithHover: [COLORS.secondary, COLORS.primaryHover],

  // Common background hover
  backgroundHover: [COLORS.backgroundHover],
  dropdownHover: [COLORS.dropdownHover],

  // Common typography
  typography: [TYPOGRAPHY.base, TYPOGRAPHY.weight],

  // Common layout patterns
  flexCenter: [LAYOUT.flex, LAYOUT.flexCenter],
  flexBetween: [LAYOUT.flex, LAYOUT.flexCenter, LAYOUT.flexBetween],

  // Common visual effects
  rounded: [EFFECTS.rounded],
  transition: [EFFECTS.transition],
  transitionColors: [EFFECTS.transitionColors],
};

// Final navigation styles built from building blocks
export const NAVIGATION_STYLES = {
  // Desktop Navigation Styles
  "desktop-link": combineClasses(
    BASE_COMBINATIONS.textWithHover,
    BASE_COMBINATIONS.backgroundHover,
    LAYOUT.paddingStandard,
    BASE_COMBINATIONS.rounded,
    BASE_COMBINATIONS.typography,
    BASE_COMBINATIONS.transition,
  ),

  "desktop-button": combineClasses(
    BASE_COMBINATIONS.textWithHover,
    BASE_COMBINATIONS.backgroundHover,
    LAYOUT.paddingStandard,
    BASE_COMBINATIONS.rounded,
    BASE_COMBINATIONS.typography,
    BASE_COMBINATIONS.transition,
    BASE_COMBINATIONS.flexCenter,
  ),

  "desktop-dropdown": combineClasses(
    LAYOUT.block,
    LAYOUT.paddingDropdown,
    BASE_COMBINATIONS.typography,
    BASE_COMBINATIONS.textWithHover,
    BASE_COMBINATIONS.dropdownHover,
    BASE_COMBINATIONS.transitionColors,
    EFFECTS.roundedFirst,
    EFFECTS.roundedLast,
  ),

  "desktop-auth": combineClasses(
    "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500",
    "text-white",
    "px-4 py-1.5",
    BASE_COMBINATIONS.rounded,
    BASE_COMBINATIONS.typography,
    BASE_COMBINATIONS.transition,
    "shadow-lg hover:shadow-xl",
    "font-medium",
  ),

  // Mobile Navigation Styles
  "mobile-link": combineClasses(
    LAYOUT.block,
    BASE_COMBINATIONS.textWithHover,
    BASE_COMBINATIONS.backgroundHover,
    LAYOUT.paddingMobile,
    BASE_COMBINATIONS.rounded,
    BASE_COMBINATIONS.typography,
    BASE_COMBINATIONS.transition,
  ),

  "mobile-button": combineClasses(
    LAYOUT.fullWidth,
    BASE_COMBINATIONS.flexBetween,
    BASE_COMBINATIONS.textWithHover,
    BASE_COMBINATIONS.backgroundHover,
    LAYOUT.paddingMobile,
    BASE_COMBINATIONS.rounded,
    BASE_COMBINATIONS.typography,
    BASE_COMBINATIONS.transition,
    STATES.group,
  ),

  "mobile-dropdown": combineClasses(
    LAYOUT.block,
    BASE_COMBINATIONS.textSecondaryWithHover,
    BASE_COMBINATIONS.backgroundHover,
    LAYOUT.paddingMobileDropdown,
    LAYOUT.marginLeft3,
    BASE_COMBINATIONS.rounded,
    BASE_COMBINATIONS.typography,
    BASE_COMBINATIONS.transition,
  ),

  "mobile-auth": combineClasses(
    "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500",
    "text-white",
    "px-3 py-1.5",
    BASE_COMBINATIONS.rounded,
    "text-sm font-medium",
    BASE_COMBINATIONS.transition,
    "shadow-lg hover:shadow-xl",
    "whitespace-nowrap",
  ),

  // Dropdown container styles
  "dropdown-container": combineClasses(
    LAYOUT.absolute,
    LAYOUT.topFull,
    LAYOUT.leftZero,
    LAYOUT.marginTop1,
    LAYOUT.width48,
    COLORS.dropdownBg,
    EFFECTS.backdrop,
    EFFECTS.border,
    COLORS.borderColor,
    BASE_COMBINATIONS.rounded,
    EFFECTS.shadow,
    BASE_COMBINATIONS.transition,
  ),

  "mobile-dropdown-container": combineClasses(
    LAYOUT.spaceY1,
    LAYOUT.marginTop1,
  ),

  // Additional mobile dropdown styles
  "mobile-dropdown-submenu":
    "transition-all duration-300 ease-in-out overflow-hidden",
  "mobile-dropdown-visible": "max-h-48 opacity-100",
  "mobile-dropdown-hidden": "max-h-0 opacity-0",

  // Icon styles for mobile dropdowns
  "mobile-icon-base": combineClasses(
    LAYOUT.height4,
    LAYOUT.height4,
    "transition-all duration-200",
  ),
};
